import { queryClient, queryKeys } from "@/client";
import { type TableRow } from "@/components/DirectorySubmissions/Form/types";
import { tableMap } from "@/components/DirectorySubmissions/Form/utils/saveData";
import { type SupabaseClient } from "@supabase/supabase-js";
import { type EnqueueSnackbar } from "notistack";

type FieldMapping = Record<string, string[]>;

const fieldMappings: FieldMapping = {
  directory_submission_contact_persons: ["name", "email", "telephone_number"],
  directory_submission_department_heads: ["name", "email", "telephone_number"],
  directory_submission_partner_changes: [
    "name",
    "joined_departed",
    "joined_from_destination",
  ],
  directory_submission_lawyer_info: [
    "name",
    "comments_or_web_link",
    "is_partner",
    "is_ranked",
    "additional_info",
  ],
  directory_submission_barristers_used: ["name", "firm_or_set", "comments"],
  directory_submission_publishable_clients: ["client_name", "is_new_client"],
  directory_submission_confidential_clients: ["client_name", "is_new_client"],
};

const transformData = (
  data: Array<Array<string | boolean | null>>,
  fields: string[],
): Array<Record<string, any>> => {
  return data.map((row) =>
    fields.reduce<Record<string, any>>((obj, field, index) => {
      obj[field] = row[index];
      if (
        field === "is_partner" ||
        field === "is_ranked" ||
        field === "is_new_client"
      ) {
        obj[field] = row[index] === null ? null : Boolean(row[index]);
      }
      return obj;
    }, {}),
  );
};

export const saveTableData = async (
  tableName: string,
  submissionId: number,
  data: TableRow[],
  firmId: string,
  supabase: SupabaseClient,
): Promise<TableRow[]> => {
  try {
    const transformedData = data.map((entry) => ({
      ...transformData([entry.value], fieldMappings[tableName])[0],
      ...(entry.id && { id: entry.id }),
      row_order: entry.row_order,
      firm_id: firmId,
      directory_submission_id: submissionId,
    }));

    if (transformedData.length > 0) {
      const { data: upsertedData, error: upsertError } = await supabase
        .from(tableName)
        .upsert(transformedData, {
          onConflict: "id",
          ignoreDuplicates: false,
        })
        .select();

      if (upsertError) {
        console.error(`Error upserting rows in ${tableName}:`, upsertError);
        throw new Error(
          `Error upserting rows in ${tableName}: ${upsertError.message}`,
        );
      }

      return upsertedData.map((item) => ({
        id: item.id,
        row_order: item.row_order,
        value: fieldMappings[tableName].map((field) => item[field] ?? ""),
      }));
    }
    return data;
  } catch (error) {
    console.error(`Error updating related table ${tableName}:`, error);
    throw new Error(`Error updating related table ${tableName}: ${error}`);
  }
};

export const saveRowOrder = async ({
  supabase,
  submissionId,
  fieldId,
  rows,
  enqueueSnackbar,
}: {
  supabase: SupabaseClient | undefined;
  submissionId: number | undefined;
  fieldId: string;
  rows: TableRow[];
  enqueueSnackbar: EnqueueSnackbar;
}) => {
  const tableName = tableMap[fieldId];
  if (!supabase || !submissionId || !tableName) {
    enqueueSnackbar("Failed to save order. Invalid configuration.", {
      variant: "error",
    });
    return;
  }

  const updates = rows.map((row, index) => ({
    id: row.id,
    row_order: index + 1,
  }));

  const { error } = await supabase.rpc("reorder_table_rows", {
    _table_name: tableName,
    _submission_id: submissionId,
    _updates: updates,
  });

  if (error) {
    console.error("Error reordering rows:", error);
    enqueueSnackbar("Failed to save new row order. Please refresh.", {
      variant: "error",
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.directorySubmission(submissionId.toString()),
    });
    return;
  }

  void queryClient.invalidateQueries({
    queryKey: queryKeys.directorySubmission(submissionId.toString()),
    exact: true,
  });
};
